import React from 'react'
import { Button, Flex, Heading, toast } from '@workwhile/ui'
import { FormProvider, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { ModalWithSelect } from 'components/ModalSelect'
import {
  invoiceConfigValuesSchema,
  InvoiceConfigValuesSchema,
  toInvoiceConfigCustomWeek,
} from '../../../schemas/invoiceConfigValues'
import { useInvoiceConfigWorkflowsContextValue } from '../../../context/InvoiceConfigWorkflowsProvider'
import { InvoiceConfigRow } from '../../../types'
import { useEditInvoiceConfigFlowContextValue } from '../context/EditInvoiceConfigFlowProvider'
import { EditInvoiceConfigForm } from './EditInvoiceConfigForm'

export const EditInvoiceConfigFlowMain = ({
  invoiceConfig,
}: {
  invoiceConfig: InvoiceConfigRow
}) => {
  const {
    editInvoiceConfigFlow: { isOpen, close },
  } = useInvoiceConfigWorkflowsContextValue()

  const {
    state: {
      mutationState: { isPending },
    },
    actions: { submit, reset: resetMutation },
  } = useEditInvoiceConfigFlowContextValue()

  const methods = useForm<InvoiceConfigValuesSchema>({
    resolver: zodResolver(invoiceConfigValuesSchema),
    values: {
      breakdownLevels: invoiceConfig.breakdownLevels,
      useCustomWeek: invoiceConfig.customWeek != null,
      weekStartDay: invoiceConfig.customWeek?.weekStartDay,
      timezone: invoiceConfig.customWeek?.timezone,
    },
  })

  const { handleSubmit, reset } = methods

  const handleClose = () => {
    reset()
    resetMutation()
    close()
  }

  const onSubmit = handleSubmit((data) => {
    submit(
      {
        invoiceConfigId: invoiceConfig.id,
        breakdownLevels: data.breakdownLevels,
        customWeek: toInvoiceConfigCustomWeek(data),
      },
      {
        onSuccess: () => {
          toast.success('Invoice config updated successfully', {
            position: 'top-center',
          })
          handleClose()
        },
      }
    )
  })

  return (
    <ModalWithSelect
      open={isOpen}
      onClose={handleClose}
      position="top"
      loading={isPending}
      showCancel
      customCta={
        <Flex>
          <Button variant="text" onClick={handleClose}>
            Cancel
          </Button>
          <Button variant="primary" onClick={onSubmit}>
            Save
          </Button>
        </Flex>
      }
    >
      <Heading level={3} fontWeight="400" style={{ margin: 0 }}>
        Edit invoice config
      </Heading>
      <FormProvider {...methods}>
        <EditInvoiceConfigForm
          invoiceConfig={invoiceConfig}
          onSubmit={onSubmit}
        />
      </FormProvider>
    </ModalWithSelect>
  )
}
